import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import FallbackImg from "../../../components/FallbackImg";
import BackBlue from "../../../assets/aftersalesservice_icons/back_blue_icon.svg";
import UserManualSafetyIcon from "../../../assets/aftersalesservice_icons/user_manual_safety_icon.svg";
import UserManualInstallIcon from "../../../assets/aftersalesservice_icons/user_manual_install_icon.svg";
import UserManualOperate from "../../../assets/aftersalesservice_icons/user_manual_operate_icon.svg";
import UserManualMaintIcon from "../../../assets/aftersalesservice_icons/user_manual_maintenance_icon.svg";
import { useLanguage } from "../LanguageContext";
import ApiService from "../../../components/Api_services";
import PreviewLayout from "./PreviewLayout";

// const lightenColor = (hex, amount = 0.5) => {
//   if (!hex) return null;

//   const clean = hex.replace("#", "");
//   const normalized =
//     clean.length === 3
//       ? clean
//           .split("")
//           .map((c) => c + c)
//           .join("")
//       : clean;
//   const value = parseInt(normalized, 16);
//   if (Number.isNaN(value) || normalized.length !== 6) return hex;

//   const r = (value >> 16) & 255;
//   const g = (value >> 8) & 255;
//   const b = value & 255;
//   const lighten = (channel) => Math.round(channel + (255 - channel) * amount);

//   return `rgb(${lighten(r)}, ${lighten(g)}, ${lighten(b)})`;
// };

const SectionIcon = ({ name, color, size = 20 }) => {
  const IconComp = name && LucideIcons[name];
  return IconComp ? (
    <IconComp
      color={color || "#000000"}
      size={size}
      style={{ flexShrink: 0 }}
    />
  ) : null;
};

const PreviewUserManual = () => {
  const { trans, lang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const itemId = searchParams.get("item_id");
  const country = searchParams.get("country") || "";
  const userManualType = searchParams.get("user_manual_type");
  const [userManual, setUserManual] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const sections = useMemo(() => {
    const item = (userManual || [])[0];
    return (item?.sections || [])
      .slice()
      .sort((a, b) => (Number(a.seq_no) || 0) - (Number(b.seq_no) || 0));
  }, [userManual]);

  useEffect(() => {
    const fetchUserManual = async () => {
      const apiLang = lang === "ch" ? "cn" : lang === "my" ? "ms" : lang;
      setLoaded(false);

      try {
        const response = await ApiService.getUserManual(
          itemId,
          apiLang,
          userManualType,
          country,
        );
        if (response && response.status === 200) {
          setUserManual(response.data);
        } else {
          setUserManual(null);
          console.error("Error fetching user manual:", response.message);
        }
      } catch (error) {
        setUserManual(null);
        console.error("Error fetching user manual:", error);
      } finally {
        setLoaded(true);
      }
    };

    fetchUserManual();
  }, [itemId, country, lang, userManualType]);

  // shown once the fetch has finished (success or error) with no sections
  const emptySections = loaded && sections.length === 0 && (
    <div className="as-user-manual-item" style={{ backgroundColor: "#ffffff" }}>
      <div className="as-no-results" style={{ display: "block" }}>
        <div>{trans.no_user_manual}</div>
      </div>
    </div>
  );

  return (
    <PreviewLayout>
      {userManualType == 1 ? (
        <div
          className="as-user-manual-safety-content"
          id="user_manual_safety_modal"
        >
          <div style={{ padding: "0 31.97px" }}>
            <div className="as-user-manual-safety-container">
              <button
                type="button"
                className="as-back-btn"
                id="user_manual_safety_back"
                onClick={() =>
                  navigate(
                    `/aftersalesservice/previewusermanual?item_id=${itemId}&country=${country}`,
                  )
                }
              >
                <FallbackImg src={BackBlue} alt="back_blue_icon" />
                {trans.back_manual}
              </button>

              <div className="as-user-manual-safety-main">
                <FallbackImg
                  src={UserManualSafetyIcon}
                  alt="user_manual_safety_icon"
                />
                <div>
                  <div className="as-user-manual-safety-main-title">
                    {trans.safety_precaut}
                  </div>
                  <div className="as-user-manual-safety-main-text">
                    {trans.safety_precaut_sub}
                  </div>
                </div>
              </div>
            </div>

            {sections.map((section) => (
              <div
                className="as-user-manual-item"
                key={section.id}
                style={{
                  backgroundColor: section.background_color
                    ? section.background_color
                    : "#ffffff",
                  color: section.text_color ? section.text_color : "#000000",
                }}
              >
                <div className="as-user-manual-title">
                  <div
                    className="as-user-manual-title-icon"
                    // style={{
                    //   backgroundColor: section.background_color
                    //     ? lightenColor(section.background_color)
                    //     : "#f0f0f0",
                    // }}
                  >
                    <SectionIcon
                      name={section.icon}
                      color={section.text_color}
                    />
                  </div>
                  <div>{section.description}</div>
                </div>
                {(section.children || []).map((child) => (
                  <div
                    className="as-user-manual-box"
                    key={child.id}
                    style={{
                      backgroundColor: child.background_color
                        ? child.background_color
                        : "transparent",
                      color: child.text_color ? child.text_color : "#000000",
                    }}
                  >
                    <SectionIcon name={child.icon} color={child.text_color} />
                    <div>{child.description}</div>
                  </div>
                ))}
              </div>
            ))}
            {emptySections}
          </div>
        </div>
      ) : userManualType == 2 ? (
        <div
          className="as-user-manual-install-content"
          id="user_manual_installation_modal"
        >
          <div style={{ padding: "0 31.97px" }}>
            <div className="as-user-manual-install-container">
              <button
                type="button"
                className="as-back-btn"
                id="user_manual_installation_back"
                onClick={() =>
                  navigate(
                    `/aftersalesservice/previewusermanual?item_id=${itemId}&country=${country}`,
                  )
                }
              >
                <FallbackImg src={BackBlue} alt="back_blue_icon" />
                {trans.back_manual}
              </button>

              <div className="as-user-manual-install-main">
                <FallbackImg
                  src={UserManualInstallIcon}
                  alt="user_manual_install_icon"
                />
                <div>
                  <div className="as-user-manual-install-main-title">
                    {trans.install_guideline}
                  </div>
                  <div className="as-user-manual-install-main-text">
                    {trans.install_guideline_sub}
                  </div>
                </div>
              </div>
            </div>

            {sections.map((section) => (
              <div
                className="as-user-manual-item"
                key={section.id}
                style={{
                  backgroundColor: section.background_color
                    ? section.background_color
                    : "#ffffff",
                  color: section.text_color ? section.text_color : "#000000",
                }}
              >
                <div className="as-user-manual-title">
                  <div
                    className="as-user-manual-title-icon"
                    // style={{
                    //   backgroundColor: section.background_color
                    //     ? lightenColor(section.background_color)
                    //     : "#f0f0f0",
                    // }}
                  >
                    <SectionIcon
                      name={section.icon}
                      color={section.text_color}
                    />
                  </div>
                  <div>{section.description}</div>
                </div>
                {(section.children || []).map((child) => (
                  <div
                    className="as-user-manual-box"
                    key={child.id}
                    style={{
                      backgroundColor: child.background_color
                        ? child.background_color
                        : "transparent",
                      color: child.text_color ? child.text_color : "#000000",
                    }}
                  >
                    <SectionIcon name={child.icon} color={child.text_color} />
                    <div>{child.description}</div>
                  </div>
                ))}
              </div>
            ))}
            {emptySections}
          </div>
        </div>
      ) : userManualType == 3 ? (
        <div
          className="as-user-manual-operate-content"
          id="user_manual_operating_modal"
        >
          <div style={{ padding: "0 31.97px" }}>
            {/* Header */}
            <div className="as-user-manual-operate-container">
              <button
                type="button"
                className="as-back-btn"
                id="user_manual_operating_back"
                onClick={() =>
                  navigate(
                    `/aftersalesservice/previewusermanual?item_id=${itemId}&country=${country}`,
                  )
                }
              >
                <FallbackImg src={BackBlue} alt="backblue" />
                {trans.back_manual}
              </button>
              <div className="as-user-manual-operate-main">
                <FallbackImg src={UserManualOperate} alt="usermanualoperate" />
                <div>
                  <div className="as-user-manual-operate-main-title">
                    {trans.operate_instruct}
                  </div>
                  <div className="as-user-manual-operate-main-text">
                    {trans.operate_instruct_sub_2}
                  </div>
                </div>
              </div>
            </div>

            {sections.map((section) => (
              <div
                className="as-user-manual-item"
                key={section.id}
                style={{
                  backgroundColor: section.background_color
                    ? section.background_color
                    : "#ffffff",
                  color: section.text_color ? section.text_color : "#000000",
                }}
              >
                <div className="as-user-manual-title">
                  <div
                    className="as-user-manual-title-icon"
                    // style={{
                    //   backgroundColor: section.background_color
                    //     ? lightenColor(section.background_color)
                    //     : "#f0f0f0",
                    // }}
                  >
                    <SectionIcon
                      name={section.icon}
                      color={section.text_color}
                    />
                  </div>
                  <div>{section.description}</div>
                </div>
                {(section.children || []).map((child) => (
                  <div
                    className="as-user-manual-box"
                    key={child.id}
                    style={{
                      backgroundColor: child.background_color
                        ? child.background_color
                        : "transparent",
                      color: child.text_color ? child.text_color : "#000000",
                    }}
                  >
                    <SectionIcon name={child.icon} color={child.text_color} />
                    <div>{child.description}</div>
                  </div>
                ))}
              </div>
            ))}
            {emptySections}
          </div>
        </div>
      ) : userManualType == 4 ? (
        <div
          className="as-user-manual-maint-content"
          id="user_manual_maintenance_modal"
        >
          <div style={{ padding: "0 31.97px" }}>
            <div className="as-user-manual-maint-container">
              <button
                type="button"
                className="as-back-btn"
                id="user_manual_maintenance_back"
                onClick={() =>
                  navigate(
                    `/aftersalesservice/previewusermanual?item_id=${itemId}&country=${country}`,
                  )
                }
              >
                <FallbackImg src={BackBlue} alt="back_blue_icon" />
                {trans.back_manual}
              </button>

              <div className="as-user-manual-maint-main">
                <FallbackImg
                  src={UserManualMaintIcon}
                  alt="user_manual_maintenance_icon"
                />
                <div>
                  <div className="as-user-manual-maint-main-title">
                    {trans.maint}
                  </div>
                  <div className="as-user-manual-maint-main-text">
                    {trans.maint_sub_2}
                  </div>
                </div>
              </div>
            </div>

            {sections.map((section) => (
              <div
                className="as-user-manual-item"
                key={section.id}
                style={{
                  backgroundColor: section.background_color
                    ? section.background_color
                    : "#ffffff",
                  color: section.text_color ? section.text_color : "#000000",
                }}
              >
                <div className="as-user-manual-title">
                  <div
                    className="as-user-manual-title-icon"
                    // style={{
                    //   backgroundColor: section.background_color
                    //     ? lightenColor(section.background_color)
                    //     : "#f0f0f0",
                    // }}
                  >
                    <SectionIcon
                      name={section.icon}
                      color={section.text_color}
                    />
                  </div>
                  <div>{section.description}</div>
                </div>
                {(section.children || []).map((child) => (
                  <div
                    className="as-user-manual-box"
                    key={child.id}
                    style={{
                      backgroundColor: child.background_color
                        ? child.background_color
                        : "transparent",
                      color: child.text_color ? child.text_color : "#000000",
                    }}
                  >
                    <SectionIcon name={child.icon} color={child.text_color} />
                    <div>{child.description}</div>
                  </div>
                ))}
              </div>
            ))}
            {emptySections}
          </div>
        </div>
      ) : null}
    </PreviewLayout>
  );
};

export default PreviewUserManual;
